import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { BarChart3, DollarSign, Briefcase, TrendingUp, Calendar } from 'lucide-react';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` });

const METHOD_LABELS = { nakit: 'Nakit', havale_eft: 'Havale/EFT', kredi_karti: 'Kredi Kartı', toptancidan: 'Toptancıdan' };
const STATUS_LABELS = {
  beklemede: 'Beklemede', randevu_planlandi: 'Randevu Planlandı', arac_kabul: 'Araç Kabul',
  islemde: 'İşlemde', kalite_kontrol: 'Kalite Kontrol', teslime_hazir: 'Teslime Hazır',
  teslim_edildi: 'Teslim Edildi', pending: 'Beklemede', in_progress: 'İşlemde',
  ready: 'Teslime Hazır', completed: 'Teslim Edildi',
};

const ReportsModule = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [jobReport, setJobReport] = useState(null);
  const [paymentReport, setPaymentReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('jobs');

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = `?start_date=${startDate}&end_date=${endDate}`;
      const [jRes, pRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/reports/jobs${params}`, { headers: getHeaders() }),
        fetch(`${BACKEND_URL}/api/reports/payments${params}`, { headers: getHeaders() }),
      ]);
      if (jRes.ok) setJobReport(await jRes.json());
      if (pRes.ok) setPaymentReport(await pRes.json());
    } catch (e) { console.error('Load reports error:', e); }
    setLoading(false);
  };

  useEffect(() => { loadReports(); }, []);

  const BarSegment = ({ items, total, colors }) => {
    if (total === 0) return <div className="h-6 bg-gray-700 rounded-full" />;
    return (
      <div className="h-6 bg-gray-700 rounded-full overflow-hidden flex">
        {items.map(([key, val], i) => (
          <div key={key} style={{ width: `${(val / total) * 100}%`, backgroundColor: colors[i % colors.length] }}
            className="h-full" title={`${key}: ${val}`} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Date Filter */}
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="text-gray-400 text-xs">Başlangıç</label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="report-start-date" />
        </div>
        <div>
          <label className="text-gray-400 text-xs">Bitiş</label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="report-end-date" />
        </div>
        <Button size="sm" onClick={loadReports} disabled={loading} data-testid="report-load-btn"
          className="bg-brand hover:bg-brand text-white h-8">
          <BarChart3 className="h-3 w-3 mr-1" />{loading ? 'Yükleniyor...' : 'Raporu Getir'}
        </Button>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => setTab('jobs')}
          className={tab === 'jobs' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}>
          <Briefcase className="h-3 w-3 mr-1" />İş Raporu
        </Button>
        <Button size="sm" onClick={() => setTab('payments')}
          className={tab === 'payments' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}>
          <DollarSign className="h-3 w-3 mr-1" />Ödeme Raporu
        </Button>
      </div>

      {/* Job Report */}
      {tab === 'jobs' && jobReport && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-white">{jobReport.totalJobs}</p>
              <p className="text-gray-400 text-xs">Toplam İş</p>
            </CardContent></Card>
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-400">{jobReport.delivered}</p>
              <p className="text-gray-400 text-xs">Teslim Edildi</p>
            </CardContent></Card>
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{jobReport.active}</p>
              <p className="text-gray-400 text-xs">Aktif</p>
            </CardContent></Card>
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-brand">{jobReport.totalRevenue?.toLocaleString()} TL</p>
              <p className="text-gray-400 text-xs">Toplam Gelir</p>
            </CardContent></Card>
          </div>

          {/* By Status */}
          {Object.keys(jobReport.byStatus || {}).length > 0 && (
            <Card className="bg-gray-800/50 border-gray-700/40"><CardContent className="p-3">
              <h4 className="text-white text-sm font-semibold mb-2">Duruma Göre</h4>
              <div className="space-y-1">
                {Object.entries(jobReport.byStatus).sort((a, b) => b[1] - a[1]).map(([st, cnt]) => (
                  <div key={st} className="flex justify-between items-center text-xs">
                    <span className="text-gray-300">{STATUS_LABELS[st] || st}</span>
                    <Badge className="bg-gray-700 text-white">{cnt}</Badge>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}

          {/* By Service */}
          {Object.keys(jobReport.byService || {}).length > 0 && (
            <Card className="bg-gray-800/50 border-gray-700/40"><CardContent className="p-3">
              <h4 className="text-white text-sm font-semibold mb-2">Servis Tipine Göre</h4>
              <div className="space-y-1">
                {Object.entries(jobReport.byService).sort((a, b) => b[1] - a[1]).map(([svc, cnt]) => (
                  <div key={svc} className="flex justify-between items-center text-xs">
                    <span className="text-brand">{svc}</span>
                    <Badge className="bg-gray-700 text-white">{cnt}</Badge>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}

          {/* By Staff */}
          {Object.keys(jobReport.byStaff || {}).length > 0 && (
            <Card className="bg-gray-800/50 border-gray-700/40"><CardContent className="p-3">
              <h4 className="text-white text-sm font-semibold mb-2">Personele Göre</h4>
              <div className="space-y-1">
                {Object.entries(jobReport.byStaff).sort((a, b) => b[1] - a[1]).map(([name, cnt]) => (
                  <div key={name} className="flex justify-between items-center text-xs">
                    <span className="text-gray-300">{name}</span>
                    <Badge className="bg-gray-700 text-white">{cnt}</Badge>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Payment Report */}
      {tab === 'payments' && paymentReport && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-400">{paymentReport.totalAmount?.toLocaleString()} TL</p>
              <p className="text-gray-400 text-xs">Toplam Tahsilat</p>
            </CardContent></Card>
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-white">{paymentReport.totalPayments}</p>
              <p className="text-gray-400 text-xs">Ödeme Sayısı</p>
            </CardContent></Card>
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-yellow-400">{paymentReport.totalDiscount?.toLocaleString()} TL</p>
              <p className="text-gray-400 text-xs">Toplam İskonto</p>
            </CardContent></Card>
            <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{paymentReport.totalOutstanding?.toLocaleString()} TL</p>
              <p className="text-gray-400 text-xs">Bekleyen Bakiye</p>
            </CardContent></Card>
          </div>

          {/* By Method */}
          {Object.keys(paymentReport.byMethod || {}).length > 0 && (
            <Card className="bg-gray-800/50 border-gray-700/40"><CardContent className="p-3">
              <h4 className="text-white text-sm font-semibold mb-2">Ödeme Yöntemine Göre</h4>
              <BarSegment items={Object.entries(paymentReport.byMethod)} total={paymentReport.totalAmount || 1}
                colors={['#22c55e', '#3b82f6', '#f97316', '#a855f7']} />
              <div className="grid grid-cols-2 gap-1 mt-2">
                {Object.entries(paymentReport.byMethod).map(([m, amt]) => (
                  <div key={m} className="flex justify-between items-center text-xs bg-gray-900/50 rounded p-1.5">
                    <span className="text-gray-300">{METHOD_LABELS[m] || m}</span>
                    <span className="text-white font-semibold">{amt.toLocaleString()} TL</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}

          {/* Recent Payments */}
          {paymentReport.payments?.length > 0 && (
            <Card className="bg-gray-800/50 border-gray-700/40"><CardContent className="p-3">
              <h4 className="text-white text-sm font-semibold mb-2">Son Ödemeler</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {paymentReport.payments.slice(0, 20).map((p, i) => (
                  <div key={p.id || `rpt-pay-${p.date}-${i}`} className="flex justify-between items-center text-xs bg-gray-900/50 rounded p-2">
                    <div>
                      <span className="text-white">{p.customerName}</span>
                      <span className="text-gray-500 ml-2">{p.carPlate}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-green-400 font-semibold">{p.amount?.toLocaleString()} TL</span>
                      <span className="text-gray-500 ml-2">{METHOD_LABELS[p.method] || p.method}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsModule;
